import { Users } from 'lucide-react';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { formatDate } from '@/lib/format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { useOpsOrgTeams, type OrgTeamRow } from '@/api/operations-org-hooks';

const columns: ColumnDef<OrgTeamRow>[] = [
  {
    id: 'team_name',
    header: 'Team name',
    accessorFn: (r) => r.team_name,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 500 }}>
        {row.original.team_name}
      </span>
    ),
  },
  {
    id: 'members',
    header: 'Members',
    accessorFn: (r) => r.members_count,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
        {row.original.members_count.toLocaleString('en-US')}
      </span>
    ),
  },
  {
    id: 'team_id',
    header: 'ID',
    accessorFn: (r) => r.id,
    cell: ({ row }) => (
      <span
        style={{
          fontFamily: 'monospace',
          fontSize: 'var(--vn-font-size-xs)',
          color: 'var(--vn-fg-muted)',
        }}
      >
        {row.original.id}
      </span>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorFn: (r) => r.created_at,
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];

/** Org detail Teams tab — list of teams whose services belong to this org. */
export function OpsOrgTeamsTab({ orgId }: { orgId: string }) {
  const { data, isLoading, isError, refetch } = useOpsOrgTeams(orgId);

  if (isError) return <TabError title="Failed to load teams" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading rows={5} />;

  return (
    <DataTable
      columns={columns}
      data={data ?? []}
      getRowId={(r) => r.id}
      emptyState={
        <EmptyState
          icon={<Users size={24} />}
          title="No teams"
          description="This organization has no teams yet."
        />
      }
    />
  );
}
